import { useState } from 'react'
import { Anchor, Badge, Button, Group, List, Paper, Radio, Stack, Table, Text, TextInput, Textarea, Title } from '@mantine/core'

// #1130 admin 応募詳細の面談調整(active な面談の表示)。状態・形式・所要時間・場所/URL・候補一覧・確定日時を表示する。
// #1133(柱4-5)キャンセル・再調整・実施済みの操作 UI を追加。
//
// 送信は通常の form POST(fetch は使わない)。操作ごとに <form action> を切り替える必要があるため、
// InterviewSlotPicker(#1131)/ JobApplicationWithdrawButton(#1122)と同じく、ボタン押下でその場に展開する
// インライン確認パネルの中で自前の <form> を組み、CSRF トークンはレイアウトの csrf_meta_tags から読む
// (Mantine の Modal はフローティング系で描画されない可能性がある。see memory: mantine_v9_broken_components)。
//
// キャンセル理由(reason)は admin の内部メモ(Interview#cancel_reason)。応募者・企業には一切見せない
// (非媒介原則。Interview::CancellationNotifier / SlackCancellationNotifier の本文には出さない)。
// 再調整(reschedule)は AdminInterviewForm と同じ入力(形式・所要時間・場所/URL・候補日時)を、
// 別の URL(reschedule)へ送る。
const INTERVIEW_STATUS_COLORS = {
  proposing: 'brand',
  confirmed: 'green',
  declined: 'red',
  cancelled: 'gray',
  done: 'gray',
}

const PANEL = { NONE: 'none', CANCEL: 'cancel', RESCHEDULE: 'reschedule' }

function Row({ label, children }) {
  return (
    <Table.Tr>
      <Table.Th w={140}>{label}</Table.Th>
      <Table.Td>{children}</Table.Td>
    </Table.Tr>
  )
}

function readCsrfMeta(name) {
  if (typeof document === 'undefined') return null
  const meta = document.querySelector(`meta[name="csrf-${name}"]`)
  return meta ? meta.content : null
}

function CsrfField() {
  const csrfParam = readCsrfMeta('param')
  const csrfToken = readCsrfMeta('token')
  if (!csrfParam || !csrfToken) return null
  return <input type="hidden" name={csrfParam} value={csrfToken} />
}

function CancelPanel({ status, cancelUrl, onClose }) {
  return (
    <Paper withBorder p="md" mt="md" component="form" method="post" action={cancelUrl}>
      <CsrfField />
      <Stack gap="md">
        <Title order={3} fz="h4">この面談をキャンセルしますか？</Title>
        <Textarea
          name="reason"
          label="キャンセル理由（内部メモ・任意）"
          description="Bizlink 担当者のみが確認します。応募者・企業には送られません。"
          placeholder="任意"
          autosize
          minRows={2}
          maxRows={6}
        />
        <Text fz="sm" c="dimmed">
          確定すると応募者{status === 'confirmed' ? '・案件担当者' : ''}へキャンセルの案内メールが送信され、Slack スレッドに追記されます。
        </Text>
        <Group justify="flex-end">
          <Button type="button" variant="default" onClick={onClose}>
            閉じる
          </Button>
          <Button type="submit" color="red">
            キャンセルを確定する
          </Button>
        </Group>
      </Stack>
    </Paper>
  )
}

function ReschedulePanel({ rescheduleUrl, formats, maxSlots, defaultDurationMinutes, minStartsAt, onClose }) {
  const [format, setFormat] = useState(formats[0]?.value ?? 'online')
  const [slots, setSlots] = useState([''])
  const filledCount = slots.filter((value) => value).length

  const updateSlot = (index, value) => setSlots((prev) => prev.map((v, i) => (i === index ? value : v)))
  const removeSlot = (index) => setSlots((prev) => (prev.length === 1 ? [''] : prev.filter((_, i) => i !== index)))
  const addSlot = () => setSlots((prev) => (prev.length < maxSlots ? [...prev, ''] : prev))

  return (
    <Paper withBorder p="md" mt="md" component="form" method="post" action={rescheduleUrl}>
      <CsrfField />
      <Stack gap="md">
        <Title order={3} fz="h4">面談を再調整しますか？</Title>
        <Text fz="sm" c="dimmed">
          現在の面談は取り下げ扱いになり、下記の新しい候補日時で提示し直します。応募者へ新しい候補日時の案内メールが送信されます。
        </Text>

        <Radio.Group name="interview[format]" label="面談形式" value={format} onChange={setFormat} required>
          <Group mt="xs" gap="md" wrap="wrap">
            {formats.map((option) => (
              <Radio key={option.value} value={option.value} label={option.label} />
            ))}
          </Group>
        </Radio.Group>

        <Group grow align="flex-start">
          <TextInput
            type="number"
            name="interview[duration_minutes]"
            label="所要時間(分)"
            defaultValue={defaultDurationMinutes}
            min={15}
            step={15}
            required
          />
          <TextInput
            name="interview[location_or_url]"
            label="場所 / URL"
            description="応募者には日時が確定した後にのみ表示されます。"
            placeholder="任意"
          />
        </Group>

        <Stack gap="xs">
          <Text fz="sm" fw={500}>
            新しい候補日時(最大 {maxSlots} 件)
          </Text>
          {slots.map((value, index) => (
            <Group key={index} align="flex-end" gap="xs" wrap="nowrap">
              <TextInput
                type="datetime-local"
                name="interview[slot_proposals_attributes][][starts_at]"
                label={`候補 ${index + 1}`}
                value={value}
                min={minStartsAt}
                step={900}
                onChange={(event) => updateSlot(index, event.currentTarget.value)}
                style={{ flex: 1 }}
              />
              <Button
                type="button"
                variant="subtle"
                color="gray"
                onClick={() => removeSlot(index)}
                disabled={slots.length === 1 && !value}
              >
                削除
              </Button>
            </Group>
          ))}
          <Group>
            <Button type="button" variant="default" onClick={addSlot} disabled={slots.length >= maxSlots}>
              候補を追加
            </Button>
          </Group>
        </Stack>

        <Textarea
          name="reason"
          label="現在の面談の取り下げ理由（内部メモ・任意）"
          placeholder="任意"
          autosize
          minRows={2}
          maxRows={4}
        />

        <Group justify="flex-end">
          <Button type="button" variant="default" onClick={onClose}>
            閉じる
          </Button>
          <Button type="submit" disabled={filledCount === 0}>
            新しい候補日時を提示する
          </Button>
        </Group>
      </Stack>
    </Paper>
  )
}

function InterviewActions({
  status,
  cancelUrl,
  completeUrl,
  rescheduleUrl,
  canComplete,
  completeDisabledReason,
  formats,
  maxSlots,
  defaultDurationMinutes,
  minStartsAt,
}) {
  const [panel, setPanel] = useState(PANEL.NONE)

  if (panel === PANEL.CANCEL) {
    return <CancelPanel status={status} cancelUrl={cancelUrl} onClose={() => setPanel(PANEL.NONE)} />
  }
  if (panel === PANEL.RESCHEDULE) {
    return (
      <ReschedulePanel
        rescheduleUrl={rescheduleUrl}
        formats={formats}
        maxSlots={maxSlots}
        defaultDurationMinutes={defaultDurationMinutes}
        minStartsAt={minStartsAt}
        onClose={() => setPanel(PANEL.NONE)}
      />
    )
  }

  return (
    <Stack gap={4} mt="md" align="flex-end">
      <Group justify="flex-end" gap="sm">
        <Button type="button" variant="outline" color="red" onClick={() => setPanel(PANEL.CANCEL)}>
          キャンセル
        </Button>
        <Button type="button" variant="default" onClick={() => setPanel(PANEL.RESCHEDULE)}>
          再調整(候補を入れ直す)
        </Button>
        {status === 'confirmed' && (
          <form method="post" action={completeUrl} style={{ display: 'inline' }}>
            <CsrfField />
            <Button type="submit" disabled={!canComplete}>
              実施済みにする
            </Button>
          </form>
        )}
      </Group>
      {status === 'confirmed' && !canComplete && completeDisabledReason && (
        <Text fz="xs" c="dimmed">
          {completeDisabledReason}
        </Text>
      )}
    </Stack>
  )
}

export default function AdminInterviewSummary({
  status,
  statusLabel,
  formatLabel,
  durationMinutes,
  locationOrUrl,
  scheduledAt,
  createdAt,
  slots = [],
  cancelUrl,
  completeUrl,
  rescheduleUrl,
  canComplete = false,
  completeDisabledReason,
  formats = [],
  maxSlots = 5,
  defaultDurationMinutes = 60,
  minStartsAt,
}) {
  const isUrl = typeof locationOrUrl === 'string' && /^https?:\/\//.test(locationOrUrl)
  const canOperate = status === 'proposing' || status === 'confirmed'

  return (
    <div>
      <Group gap="sm" mb="sm" wrap="wrap">
        <Badge color={INTERVIEW_STATUS_COLORS[status] || 'gray'} variant="light" radius="sm" size="lg">
          {statusLabel}
        </Badge>
        <Text fz="sm" c="dimmed">
          起票: {createdAt}
        </Text>
      </Group>

      <Table withRowBorders={false} verticalSpacing="xs">
        <Table.Tbody>
          {scheduledAt && <Row label="確定日時">{scheduledAt}</Row>}
          <Row label="面談形式">{formatLabel}</Row>
          <Row label="所要時間">{durationMinutes} 分</Row>
          <Row label="場所 / URL">
            {locationOrUrl ? (
              isUrl ? (
                <Anchor href={locationOrUrl} target="_blank" rel="noopener noreferrer" fz="sm">
                  {locationOrUrl}
                </Anchor>
              ) : (
                locationOrUrl
              )
            ) : (
              <Text c="dimmed" span>(未入力)</Text>
            )}
          </Row>
          <Row label="候補日時">
            {slots.length === 0 ? (
              <Text c="dimmed" span>(なし)</Text>
            ) : (
              <List type="ordered" spacing={4} fz="sm">
                {slots.map((slot) => (
                  <List.Item key={slot.id}>
                    {slot.startsAt} 〜 {slot.endsAt}
                  </List.Item>
                ))}
              </List>
            )}
          </Row>
        </Table.Tbody>
      </Table>

      {status === 'proposing' && (
        <Text fz="sm" c="dimmed" mt="xs">
          応募者の候補選択待ちです。
        </Text>
      )}

      {canOperate && (
        <InterviewActions
          status={status}
          cancelUrl={cancelUrl}
          completeUrl={completeUrl}
          rescheduleUrl={rescheduleUrl}
          canComplete={canComplete}
          completeDisabledReason={completeDisabledReason}
          formats={formats}
          maxSlots={maxSlots}
          defaultDurationMinutes={defaultDurationMinutes}
          minStartsAt={minStartsAt}
        />
      )}
    </div>
  )
}
